import { Injectable, inject } from '@angular/core';
import {
  CategoryAirPlaySecurityConfigurationService,
  CategoryAirPlaySecurityProfileConfiguration,
} from '@era/settings-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, concat, from } from 'rxjs';
import { concatMap, filter, first, map, mergeMap } from 'rxjs/operators';

import { safeMap } from '../effects.helpers';
import { getAncestors } from '../router/category';
import {
  CategoryAirplaySecurityProfilePresetConfigurationApiActions,
  CategoryAirplaySecurityProfilePresetConfigurationResolverActions,
  CategoryAirplaySecurityProfilePresetConfigurationViewActions,
} from './category-airplay-security-profile-preset-configuration.actions';
import { selectAirplaySecurityProfilePresetConfigurationLinkedToCategory } from './category-airplay-security-profile-preset-configuration.selector';

@Injectable()
export class CategoryAirplaySecurityProfilePresetConfigurationEffects {
  private readonly actions$ = inject(Actions);
  private readonly categoryAirplaySecurityConfigurationService = inject(CategoryAirPlaySecurityConfigurationService);
  private readonly store = inject(Store);

  // Our API supports 0-N relations between categories and configurations. Our UI supports 0-1 relations. So we always pick
  // the first relation from the API when loading them.
  loadCategoryAirplaySecurityProfileEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        CategoryAirplaySecurityProfilePresetConfigurationResolverActions.categoryProfilePresetConfigurationRequested
      ),
      mergeMap((action) =>
        from(getAncestors(action.category)).pipe(
          mergeMap((ancestor) =>
            safeMap(
              this.categoryAirplaySecurityConfigurationService
                .getAirPlaySecurityProfilesForCategory({
                  categoryType: ancestor.categoryType,
                  categoryId: ancestor.categoryId,
                })
                .pipe(
                  filter((configurations) => configurations.length > 0),
                  map((configurations) => configurations[0])
                ),
              (configuration) =>
                CategoryAirplaySecurityProfilePresetConfigurationApiActions.categoryProfilePresetConfigurationLoaded({
                  configuration,
                  category: ancestor,
                }),
              (error) =>
                CategoryAirplaySecurityProfilePresetConfigurationApiActions.categoryProfilePresetConfigurationLoadedError(
                  {
                    error,
                  }
                )
            )
          )
        )
      )
    );
  });

  linkAirplaySecurityProfileToCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAirplaySecurityProfilePresetConfigurationViewActions.profilePresetConfigurationSelected),
      concatMap((action) => {
        const categoryType = action.category.categoryType;

        return this.store.select(selectAirplaySecurityProfilePresetConfigurationLinkedToCategory(action.category)).pipe(
          first(),
          concatMap((existingConfig) => {
            if (existingConfig?.airPlaySecurityProfilePresetId === action.preset.uniqueId) {
              return EMPTY;
            }

            const category = action.category;

            const delete$: Observable<Action> = existingConfig
              ? safeMap(
                  this.categoryAirplaySecurityConfigurationService.deleteAirPlaySecurityProfileFromCategory({
                    categoryType,
                    categoryId: category.categoryId,
                  }),
                  () =>
                    CategoryAirplaySecurityProfilePresetConfigurationApiActions.categoryProfilePresetConfigurationDeleted(
                      {
                        configuration: existingConfig,
                        category,
                      }
                    ),
                  (error) =>
                    CategoryAirplaySecurityProfilePresetConfigurationApiActions.categoryProfilePresetConfigurationDeletedError(
                      {
                        configuration: existingConfig,
                        category,
                        error,
                      }
                    )
                )
              : EMPTY;

            const fallbackConfiguration: CategoryAirPlaySecurityProfileConfiguration = {
              categoryType,
              categoryId: category.categoryId,
              airPlaySecurityProfilePresetId: action.preset.uniqueId,
              displayName: '',
            };
            const create$: Observable<Action> = safeMap(
              this.categoryAirplaySecurityConfigurationService.addAirPlaySecurityProfileToCategory({
                categoryType,
                categoryId: category.categoryId,
                addAirPlaySecurityProfileToCategoryCommand: {
                  airPlaySecurityProfilePresetId: action.preset.uniqueId,
                },
              }),
              (configuration) =>
                CategoryAirplaySecurityProfilePresetConfigurationApiActions.categoryProfilePresetConfigurationAdded({
                  configuration,
                  category,
                }),
              (error) =>
                CategoryAirplaySecurityProfilePresetConfigurationApiActions.categoryProfilePresetConfigurationAddedError(
                  {
                    configuration: fallbackConfiguration,
                    category,
                    error,
                  }
                )
            );

            return concat(delete$, create$);
          })
        );
      })
    );
  });

  deleteLinkedAirplaySecurityProfileFromCategoryEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryAirplaySecurityProfilePresetConfigurationViewActions.profilePresetConfigurationDeselected),
      mergeMap((action) => {
        const category = action.category;
        const categoryType = category.categoryType;

        return this.store.select(selectAirplaySecurityProfilePresetConfigurationLinkedToCategory(category)).pipe(
          first(),
          mergeMap((configuration) => {
            if (!configuration) {
              return EMPTY;
            }

            return safeMap(
              this.categoryAirplaySecurityConfigurationService.deleteAirPlaySecurityProfileFromCategory({
                categoryType,
                categoryId: category.categoryId,
              }),
              () =>
                CategoryAirplaySecurityProfilePresetConfigurationApiActions.categoryProfilePresetConfigurationDeleted({
                  configuration,
                  category,
                }),
              (error) =>
                CategoryAirplaySecurityProfilePresetConfigurationApiActions.categoryProfilePresetConfigurationDeletedError(
                  {
                    configuration,
                    category,
                    error,
                  }
                )
            );
          })
        );
      })
    );
  });
}
